import { useRef } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../../lang";
import { motion, useScroll, useTransform, useSpring, useMotionValue, useReducedMotion } from "framer-motion";

const soft = { stiffness: 70, damping: 24, mass: 0.5 };

export default function ParallaxTile({ to, image, title, subtitle, testId, index = 0 }) {
  const ref = useRef(null);
  const still = useReducedMotion();
  const { lang } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const p = useSpring(scrollYProgress, soft);
  const imgY = useTransform(p, [0, 1], still ? ["0%", "0%"] : ["-7%", "7%"]);
  const imgScale = useTransform(p, [0, 0.5, 1], still ? [1, 1, 1] : [1.18, 1.06, 1.12]);
  const textY = useTransform(p, [0, 0.5], still ? [0, 0] : [50, 0]);
  const textO = useTransform(p, [0.05, 0.35], [0, 1]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [2.5, -2.5]), soft);
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-3, 3]), soft);
  const gx = useTransform(mx, [-0.5, 0.5], ["30%", "70%"]);
  const glow = useTransform(gx, (x) => `radial-gradient(600px circle at ${x} 30%, rgba(255,255,255,0.10), transparent 60%)`);
  const move = (e) => {
    if (still || e.pointerType !== "mouse") return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const leave = () => { mx.set(0); my.set(0); };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 1, delay: index * 0.12, ease: [0.19, 1, 0.22, 1] }}
      className="[perspective:1600px]"
      data-testid={testId}
    >
      <motion.div style={{ rotateX: rx, rotateY: ry }} onPointerMove={move} onPointerLeave={leave} className="will-change-transform">
        <Link to={to} className="group block relative overflow-hidden rounded-3xl bg-[#0b0f1a] isolate [transform:translateZ(0)] [-webkit-mask-image:-webkit-radial-gradient(white,black)]">
          <div className="relative h-[420px] md:h-[560px] w-full overflow-hidden">
            <motion.img style={{ y: imgY, scale: imgScale }} src={image} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90 will-change-transform" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <motion.div style={{ background: glow }} className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>
          <motion.div style={{ y: textY, opacity: textO }} className="absolute inset-x-0 top-10 md:top-16 text-center px-6">
            <h3 className="text-[28px] md:text-[40px] font-semibold tracking-[-0.02em] text-white leading-tight">{title}</h3>
            {subtitle && <p className="mt-3 text-[15px] md:text-[17px] text-white/85 max-w-md mx-auto">{subtitle}</p>}
            <div className="mt-5 inline-flex items-center gap-1 text-white text-[14px]">
              <span>{lang === "it" ? "Scopri" : "Discover"}</span> <span className="transition-transform duration-300 group-hover:translate-x-1">›</span>
            </div>
          </motion.div>
        </Link>
      </motion.div>
    </motion.div>
  );
}
